/**
 * Recovers AppKit / WalletConnect from a stuck state after an invalid
 * Concordium network switch (e.g. MetaMask connected, user switched to CCD).
 */

import {
  ConnectionController,
  ChainController,
} from "@reown/appkit-controllers";
import { ConstantsUtil as CommonConstantsUtil } from "@reown/appkit-common";
import { CCD_NAMESPACE } from "./ccdConstants.js";
import {
  sessionHasCcdNamespace,
  sessionIsGenericOnly,
} from "./ccdSessionUtils.js";

let recoveryPromise = null;

function isActiveConcordiumNamespace() {
  const activeNamespace = ChainController?.state?.activeChain;
  const activeNetwork = ChainController?.state?.activeCaipNetwork;
  return (
    activeNamespace === CCD_NAMESPACE ||
    activeNetwork?.chainNamespace === CCD_NAMESPACE
  );
}

function isBrokenProvider(provider) {
  if (!provider) {
    return false;
  }
  if (!provider.client) {
    return true;
  }
  const topic = provider.session?.topic;
  if (topic === undefined || topic === null || topic === "") {
    return Boolean(provider.session);
  }
  return false;
}

/**
 * Detects and repairs stuck WC / Concordium adapter state.
 * Safe to call repeatedly (deduped).
 */
export async function recoverStuckWalletConnectState() {
  if (recoveryPromise) {
    return recoveryPromise;
  }

  recoveryPromise = (async () => {
    try {
      const { concordiumAdapter } = await import("@/utils/wagmi.config");

      if (typeof concordiumAdapter.recoverFromStuckState === "function") {
        const result = await concordiumAdapter.recoverFromStuckState();
        if (result?.recovered) {
          return result;
        }
      }

      const provider = concordiumAdapter.universalProvider;
      const onCcd = isActiveConcordiumNamespace();
      const genericOnly = sessionIsGenericOnly(provider);
      const hasCcd = sessionHasCcdNamespace(provider);
      const broken = isBrokenProvider(provider);

      if (!onCcd && !broken && !genericOnly) {
        return { recovered: false };
      }

      if (onCcd && (genericOnly || !hasCcd || broken)) {
        if (typeof ChainController.setActiveNamespace === "function") {
          const evm = CommonConstantsUtil.CHAIN.EVM;
          ChainController.setActiveNamespace(evm);
          const networks = ChainController.getCaipNetworks?.(evm);
          if (networks?.[0]) {
            ChainController.setActiveCaipNetwork?.(networks[0]);
          }
        }
        try {
          ConnectionController.resetWcConnection?.();
        } catch {
          // ignore
        }
        return { recovered: true, reason: "namespace_mismatch" };
      }

      if (broken) {
        try {
          ConnectionController.resetWcConnection?.();
        } catch {
          // ignore
        }
        return { recovered: true, reason: "broken_provider" };
      }

      return { recovered: false };
    } catch (error) {
      console.error("[WalletConnectRecovery] Failed:", error);
      return { recovered: false, error };
    } finally {
      recoveryPromise = null;
    }
  })();

  return recoveryPromise;
}

/**
 * Run recovery once after AppKit initializes (patch rollout / returning users).
 */
export function scheduleWalletConnectRecovery() {
  const run = () => {
    recoverStuckWalletConnectState().catch(() => {});
  };

  if (typeof window !== "undefined") {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(run, { timeout: 3000 });
    } else {
      setTimeout(run, 500);
    }
  }
}
